/**
 * UserPermissionsContext for PWA
 * 
 * Minimal version for PWA users (consumers, not admins).
 * PWA users are typically regular customers, so admin flags are false.
 * 
 * This context provides compatibility with shared components that
 * expect UserPermissionsContext to exist.
 * 
 * OPTIMIZED: Includes isBlocked to eliminate separate query in useBlockedCheck
 */

import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface UserPermissionsData {
  // Permission flags - PWA users are NOT admins
  isSuperadmin: boolean;
  isCustomerAdmin: boolean;
  isPartnerAdmin: boolean;
  userRole: string;
  
  // User profile data
  displayName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  tenantId: string | null;
  
  // User blocked status (consolidated - eliminates separate query)
  isBlocked: boolean;
  
  // Status
  isLoading: boolean;
}

const defaultValue: UserPermissionsData = {
  isSuperadmin: false,
  isCustomerAdmin: false,
  isPartnerAdmin: false,
  userRole: 'consumer',
  displayName: null,
  organizationId: null,
  organizationName: null,
  tenantId: null,
  isBlocked: false,
  isLoading: true,
};

const UserPermissionsContext = createContext<UserPermissionsData>(defaultValue);

interface UserPermissionsProviderProps {
  children: ReactNode;
}

export function UserPermissionsProvider({ children }: UserPermissionsProviderProps) {
  const { user, loading: authLoading, isGuest, guestData } = useAuth();

  // Fetch organization info for authenticated users
  // OPTIMIZED: Include is_blocked to eliminate separate query
  const { data: orgData, isLoading: orgLoading } = useQuery({
    queryKey: ['pwa-user-org', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get user's profile - always fetch displayName and is_blocked
      const { data: profile } = await supabase
        .from('profiles_v2')
        .select('organization_id, display_name, is_blocked')
        .eq('id', user.id)
        .single();

      if (!profile) return null;

      // Get organization details only if organization_id exists
      let orgInfo = null;
      if (profile.organization_id) {
        const { data: org } = await supabase
          .from('organizations')
          .select('id, name, tenant_id')
          .eq('id', profile.organization_id)
          .single();
        orgInfo = org;
      }

      return {
        organizationId: orgInfo?.id || profile.organization_id || null,
        organizationName: orgInfo?.name || null,
        tenantId: orgInfo?.tenant_id || null,
        displayName: profile.display_name || null,
        isBlocked: profile.is_blocked || false,
      };
    },
    enabled: !!user?.id && !isGuest,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  const value = useMemo<UserPermissionsData>(() => {
    // For guest users, use guest data
    if (isGuest && guestData) {
      return {
        isSuperadmin: false,
        isCustomerAdmin: false,
        isPartnerAdmin: false,
        userRole: 'guest',
        displayName: null,
        organizationId: guestData.organizationId || null,
        organizationName: null,
        tenantId: null,
        isBlocked: false, // Guests cannot be blocked
        isLoading: false,
      };
    }

    // For authenticated users
    if (user) {
      return {
        isSuperadmin: false,
        isCustomerAdmin: false,
        isPartnerAdmin: false,
        userRole: 'consumer',
        displayName: orgData?.displayName || user.displayName || null,
        organizationId: orgData?.organizationId || null,
        organizationName: orgData?.organizationName || null,
        tenantId: orgData?.tenantId || null,
        isBlocked: orgData?.isBlocked || false,
        isLoading: authLoading || orgLoading,
      };
    }

    // Not authenticated
    return {
      ...defaultValue,
      isLoading: authLoading,
    };
  }, [user, isGuest, guestData, orgData, authLoading, orgLoading]);

  return (
    <UserPermissionsContext.Provider value={value}>
      {children}
    </UserPermissionsContext.Provider>
  );
}

export function useUserPermissions() {
  const context = useContext(UserPermissionsContext);
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within UserPermissionsProvider');
  }
  return context;
}
